import AddIcon from '@atlaskit/icon/core/migration/add';
import { Flex } from '@atlaskit/primitives';
import Textfield from '@atlaskit/textfield';
import { submitFormOnEnter } from '@databases/form/Form';
import { SeamlessTextfield } from '@databases/form/styled';
import { messages } from '@databases/intl/messages';
import { useTextDelta } from '@databases/react/useTextDelta';
import { DatabaseFieldType, RawDatabaseValue } from '@databases/types/database';
import { MultiValueChangeType } from '@databases/utils/database/changesets';
import { generateId } from '@databases/utils/utils';
import { noop } from 'lodash-es';
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl-next';

import { PopoverEdit } from '../PopoverEdit';
import { Container } from '../TextValueEdit';
import { EditComponentProps } from '../types';

import { CurrentDataSectionValue } from './HyperlinkValue';

type Value = RawDatabaseValue<DatabaseFieldType.HYPERLINK>[0];

const HyperlinkValuePopoverEdit: FunctionComponent<
  EditComponentProps<DatabaseFieldType.HYPERLINK>
> = ({
  onChange,
  onFocus,
  onBlur,
  autoFocus,
  value,
  field,
  isInvalid,
  isSeamless,
  contentId,
  isDefaultValue,
  isFilterValueEdit,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const multiTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [hasCurrentData, setHasCurrentData] = useState<boolean>(false);
  const { formatMessage } = useIntl();

  // On initial render, determine current data exists
  useEffect(() => {
    setHasCurrentData(value.length > 0 && value.some((link) => link.url !== ''));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useTextDelta(
    textareaRef.current,
    useCallback(
      (delta, newValue) => {
        const singleLink: RawDatabaseValue<DatabaseFieldType.HYPERLINK>[0] =
          value.length === 0
            ? { _id: generateId(), url: newValue }
            : { _id: value[0]._id, url: newValue };
        onChange([singleLink], {
          type: DatabaseFieldType.HYPERLINK,
          delta:
            value.length === 0
              ? {
                  type: MultiValueChangeType.APPEND,
                  value: singleLink,
                }
              : {
                  type: MultiValueChangeType.EDIT,
                  index: 0,
                  delta,
                },
        });
      },
      [onChange, value]
    )
  );

  const Component = isSeamless ? SeamlessTextfield : Textfield;
  // Check for length greater than one ensures that existing multi value cells
  // will remain multi value regardless of the field setting
  const isMulti = field.settings.multi || value.length > 1;
  const addLinkMessage = formatMessage(
    messages['hyperlink-value-popover-edit.add-hyperlink-value']
  );

  const appendMultiValueHyperlink = React.useCallback(
    (val: Value, allValues: Value[]) => {
      if (!isMulti || val.url === '') {
        return;
      }
      onChange(allValues, {
        type: DatabaseFieldType.HYPERLINK,
        delta: {
          type: MultiValueChangeType.APPEND,
          value: val,
        },
      });
    },
    [onChange, isMulti]
  );

  const handleAddDataSectionKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      submitFormOnEnter(e);
    },
    []
  );

  const handleAddDataSectionBlur = useCallback(
    (event: React.FocusEvent<HTMLTextAreaElement, Element>) => {
      // If some text exists, append it to the value array
      if (event.target.value) {
        const newLink = { _id: generateId(), url: event.target.value };
        // Note: single value adds are handled with useTextDelta()
        appendMultiValueHyperlink(newLink, value);
        setHasCurrentData(true);
      }
      event.target.value = ''; // Ensure no text lingers in addDataSection
      onBlur?.();
    },
    [value, onBlur, appendMultiValueHyperlink]
  );

  const addDataSection = (
    <Flex justifyContent={'center'} alignItems={'center'}>
      <AddIcon label="" />
      <Container>
        <Component
          placeholder={addLinkMessage}
          onChange={noop}
          onFocus={onFocus}
          onBlur={handleAddDataSectionBlur}
          onKeyDown={handleAddDataSectionKeyDown}
          autoFocus={autoFocus}
          isInvalid={isInvalid}
          ref={isMulti ? multiTextareaRef : textareaRef}
        />
      </Container>
    </Flex>
  );

  const currentDataSection = (
    <>
      {value.map((link) => (
        <CurrentDataSectionValue
          key={link._id}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          autoFocus={autoFocus}
          value={value}
          link={link}
          setHasCurrentData={setHasCurrentData}
          contentId={contentId}
          field={field}
          isMulti={isMulti}
          multiAddDataSectionRef={multiTextareaRef}
        />
      ))}
    </>
  );

  return (
    <PopoverEdit
      isMultiSelect={isMulti}
      hasCurrentData={isMulti ? value.length > 0 : hasCurrentData}
      currentDataSection={currentDataSection}
      addDataSection={addDataSection}
      isRenderedInSidePanel={isDefaultValue || isFilterValueEdit}
      fieldWidth={field.width}
    />
  );
};

export default HyperlinkValuePopoverEdit;
